import type { State } from '../../../../Flux/Slice/index.ts'
import type { NO_TRAIT_MODE, SCALINGMODE, SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import type { UnknownAction } from '@reduxjs/toolkit'
import type { TaxaAggregationResult } from '../Sinks/Selector/TaxaAggregation.ts'
import type { FilteredBucketValues } from '../Sinks/Selector/CubeFiltering.ts'
import type { TaxaStackedBarSeries } from './Impl/TaxaStackedBarSeries.ts'
import type { TaxonEntry } from '../Sources/TaxaNavigatorPanel.tsx'

export type TransformResult<T> =
  | { status: 'idle' | 'loading'; value?: undefined; error?: undefined }
  | { status: 'ready'; value: T; error?: undefined }
  | { status: 'error'; value?: undefined; error: unknown }

export const IDLE: TransformResult<unknown> = { status: 'idle' }

export type KronaFilterOpts = {
  selectedPath: string[]
  gradeThreshold: number
  scalingMode?: SCALINGMODE
}

export type KronaTraitFilterOpts = KronaFilterOpts & {
  traitGroup: string
  noTraitMode: NO_TRAIT_MODE
}

/**
 * Ranked Bar filter opts: the shared krona filter plus an optional `rankLevel` — a taxonomy rank
 * NAME (`domain … species`) that, when set, forces the ranking onto that uniform rank instead of
 * the default (entry-children when zoomed, leaf organisms at root). See `aggregateRankedBar`.
 */
export type RankedBarOpts = KronaFilterOpts & {
  rankLevel?: string
}

export type TransformerSpecMap = {
  kronaFilteredBuckets: {
    opts: KronaFilterOpts
    result: FilteredBucketValues[]
  }

  taxaAggregation: {
    opts: KronaFilterOpts
    result: TaxaAggregationResult
  }

  taxaAggregationSingle: {
    opts: KronaFilterOpts
    result: TaxaAggregationResult
  }

  // Single-bucket aggregation by DEEPEST-resolved taxon (leaf organism), for the Ranked Bar sink.
  // Same shape as `taxaAggregationSingle`, so `rankTopTaxa` (sort + top-N) consumes it identically.
  taxaLeafAggregationSingle: {
    opts: KronaFilterOpts
    result: TaxaAggregationResult
  }

  // Ranked Bar aggregation: mode-dispatched (rankLevel > entry-children > leaf). Same result shape
  // as `taxaLeafAggregationSingle`, so `rankTopTaxa` consumes it identically.
  taxaRankedBarSingle: {
    opts: RankedBarOpts
    result: TaxaAggregationResult
  }

  taxaStackedBarSeries: {
    opts: KronaFilterOpts
    result: TaxaStackedBarSeries
  }

  traitStackedBarSeries: {
    opts: KronaTraitFilterOpts
    result: TaxaStackedBarSeries
  }

  // Single-bucket trait composition. Returns the SAME shape as `taxaAggregationSingle` so the
  // single-bucket sinks (Pie, Treemap) consume it with no render change — only a transformer swap.
  traitAggregationSingle: {
    opts: KronaTraitFilterOpts
    result: TaxaAggregationResult
  }

  taxaNavigatorIndex: {
    opts: KronaFilterOpts
    result: TaxonEntry[]
  }

  // The zoom path a breakdown chart should auto-open at: the first point where the filtered taxonomy
  // branches (longest common prefix of the surviving lineages), so a single-chain prefix like
  // Eukaryota › Arthropoda › Insecta isn't shown as one uninformative segment. Equals the input path
  // when there's no single chain to descend. See TaxaAutoDescendTarget.
  taxaAutoDescendTarget: {
    opts: KronaFilterOpts
    result: string[]
  }
}

export type TransformerKey = keyof TransformerSpecMap
export type Opts<K extends TransformerKey> = TransformerSpecMap[K]['opts']
export type Val<K extends TransformerKey> = TransformerSpecMap[K]['result']

export type BaseTransformer<K extends TransformerKey> = {
  /**
   * Hash the options passed in to the transformer to avoid recomputations when callers forget to pass memoized opts
   *
   * @param opts
   */
  optsKey: (opts: Opts<K>) => string // stable representation of opts
}

/**
 * A transformer based on useSelector statements without performing any heavy async lifting
 */
export type SelectorTransformer<K extends TransformerKey> = {
  kind: 'selector'
  select: (state: typeof State, sourceId: SourceId, bucketKey: string | undefined, opts: Opts<K>) => TransformResult<Val<K>>
} & BaseTransformer<K>

/**
 * A transformer doing heavy async lifting
 */
export type AsyncTransformer<K extends TransformerKey> = {
  kind: 'async'
  // how to compute the cache key (must include invalidation/version info)
  makeCacheKey: (state: typeof State, sourceId: SourceId, bucketKey: string | undefined, opts: Opts<K>, optsKey: string) => string
  // selectors into the cache slice
  selectEntry: (state: typeof State, cacheKey: string) => TransformResult<Val<K>>
  // action to request/ensure computation (epic dedups by cacheKey)
  request: (payload: { cacheKey: string; sourceId: SourceId; bucketKey: string | undefined; opts: Opts<K> }) => UnknownAction
} & BaseTransformer<K>

export type TransformerDef<K extends TransformerKey> = SelectorTransformer<K> | AsyncTransformer<K>

export type TransformerRegistry = {
  [K in TransformerKey]: TransformerDef<K>
}
